import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ensureAnonymousAuth } from '../../../shared/storage/firebase';
import '../styles/BillingPortalPage.css';

type PortalInvoice = {
  id: string;
  partnerId?: string;
  locationId?: string;
  schoolId?: string;
  termId: string;
  schoolName: string;
  termLabel: string;
  internalInvoiceNumber: string;
  invoiceDate: string | null;
  paymentDueDate: string | null;
  pdfUrl: string;
  canceled?: boolean;
};

type PortalDownloads = {
  invoicePdfUrl: string;
  w9Url: string;
  achUrl: string;
};

export default function BillingPortalPage() {
  const [sp] = useSearchParams();
  const token = String(sp.get('token') || '').trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<PortalInvoice | null>(null);
  const [downloads, setDownloads] = useState<PortalDownloads | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [canceledMessage, setCanceledMessage] = useState<string | null>(null);

  const [method, setMethod] = useState<'ach' | 'check' | 'card' | 'portal' | 'other'>('ach');
  const [amountDollars, setAmountDollars] = useState<string>('');
  const [paidDate, setPaidDate] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedMsg, setSubmittedMsg] = useState<string | null>(null);

  const functions = useMemo(() => getFunctions(), []);

  const load = useCallback(async () => {
    if (!token) {
      setError('Missing token.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setSubmittedMsg(null);
    try {
      await ensureAnonymousAuth();
      const fn = httpsCallable(functions, 'billingPortalGetInvoice');
      const res: any = await fn({ token });
      const data = res?.data || {};
      setInvoice(data.invoice || null);
      setDownloads(data.downloads || null);
      setExpiresAt(data.link?.expiresAt || null);
      setCanceledMessage(data.canceled ? String(data.message || 'This invoice was canceled. No payment is requested.') : null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load invoice.');
    } finally {
      setLoading(false);
    }
  }, [functions, token]);

  useEffect(() => {
    load();
  }, [load]);

  const submitPayment = useCallback(async () => {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    setSubmittedMsg(null);
    try {
      await ensureAnonymousAuth();
      const amount = amountDollars.trim() === '' ? null : Number(amountDollars);
      const amountCents = amount == null || Number.isNaN(amount) ? null : Math.round(amount * 100);
      const fn = httpsCallable(functions, 'billingPortalSubmitPayment');
      await fn({
        token,
        payment: {
          method,
          amountCents,
          paidDate: paidDate || null,
          notes: notes || null,
        },
      });
      setSubmittedMsg('✅ Payment confirmation received. Our team will review and mark the invoice as paid once funds are received.');
    } catch (e: any) {
      setError(e?.message || 'Failed to submit payment.');
    } finally {
      setSubmitting(false);
    }
  }, [amountDollars, functions, method, notes, paidDate, token]);

  return (
    <div className="portal-page">
      <div className="portal-card">
        <div className="portal-header">
          <div>
            <div className="portal-title">Billing Portal</div>
            <div className="portal-subtitle">Read-only invoice access + payment confirmation</div>
          </div>
          {expiresAt ? (
            <div className="portal-expiry">Link valid until: {new Date(expiresAt).toLocaleDateString()}</div>
          ) : null}
        </div>

        {error ? <div className="portal-error">{error}</div> : null}
        {submittedMsg ? <div className="portal-info">{submittedMsg}</div> : null}

        {loading ? (
          <div className="portal-muted">Loading…</div>
        ) : !invoice ? (
          <div className="portal-muted">No invoice data.</div>
        ) : (
          <>
            {invoice.canceled || canceledMessage ? (
              <div className="portal-info" role="status">
                <strong>{canceledMessage || 'This invoice was canceled. No payment is requested.'}</strong>
              </div>
            ) : null}
            <div className="portal-section">
              <div className="portal-section-title">{invoice.schoolName}</div>
              <div className="portal-muted">
                {invoice.termLabel || invoice.termId} · Invoice #{invoice.internalInvoiceNumber}
              </div>
              {!invoice.canceled ? <div className="portal-kv">
                <div><b>Invoice date:</b> {invoice.invoiceDate || '—'}</div>
                <div><b>Due date:</b> {invoice.paymentDueDate || '—'}</div>
              </div> : null}
            </div>

            {!invoice.canceled ? <div className="portal-section">
              <div className="portal-section-title">Downloads</div>
              <div className="portal-links">
                <a href={downloads?.invoicePdfUrl || invoice.pdfUrl} target="_blank" rel="noreferrer">Download invoice PDF</a>
                <a href={downloads?.w9Url || '#'} target="_blank" rel="noreferrer" aria-disabled={!downloads?.w9Url} className={!downloads?.w9Url ? 'disabled' : ''}>Download W‑9</a>
                <a href={downloads?.achUrl || '#'} target="_blank" rel="noreferrer" aria-disabled={!downloads?.achUrl} className={!downloads?.achUrl ? 'disabled' : ''}>Download ACH instructions</a>
              </div>
            </div> : null}

            {!invoice.canceled ? <div className="portal-section">
              <div className="portal-section-title">Payment confirmation</div>
              <div className="portal-muted">
                Have you already sent payment? Use the form below to confirm how and when payment was sent so our finance team can reconcile it.
                <br />
                Submitting does not process or charge payment.
              </div>
              <div className="portal-form">
                <div className="field">
                  <label>Payment method used</label>
                  <select value={method} onChange={(e) => setMethod(e.target.value as any)} disabled={submitting}>
                    <option value="ach">ACH</option>
                    <option value="check">Check</option>
                    <option value="card">Card</option>
                    <option value="portal">Portal</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="field">
                  <label>Amount sent (optional)</label>
                  <input
                    value={amountDollars}
                    onChange={(e) => setAmountDollars(e.target.value)}
                    placeholder="e.g. full invoice amount or partial payment"
                    disabled={submitting}
                  />
                </div>
                <div className="field">
                  <label>Date payment was sent (optional)</label>
                  <input type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} disabled={submitting} />
                </div>
                <div className="field">
                  <label>Reference / notes (optional)</label>
                  <textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Check #12345 • District payment portal • Partial payment explanation"
                    disabled={submitting}
                  />
                </div>
                <button onClick={submitPayment} disabled={submitting || !token}>
                  {submitting ? 'Confirming…' : 'Confirm payment sent'}
                </button>
              </div>
            </div> : null}
          </>
        )}
      </div>
    </div>
  );
}
